import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { track } from "../lib/mixpanel"
import { formatShortCurrency, formatCurrency } from "../lib/chart_formatters"

// Phase 2.3 Tier 2 — Cash Flow S-curve. Mirrors eva_charts_controller:
// lazy init on canvas-visible, today/end vertical lines, formatter-
// based tooltip/legend. Adds a Cumulative / Per-Period toggle. In
// cumulative mode the chart draws four running-sum lines (Actual /
// Projected / Override / Total) — the S-curve shape. In per-period
// mode it draws stacked bars of Actual / Projected / Override with a
// Total line on top so a viewer reading one bucket can see both the
// mix and the grand total.
//
// Series JSON shape (from `BudgetsHelper#cash_flow_chart_series_json`):
//   { grain, buckets: [iso…],
//     actual, projected, override, total,
//     cumulative_actual, cumulative_projected,
//     cumulative_override, cumulative_total }
export default class extends Controller {
  static targets = ["canvas", "toggleButton"]
  static values = {
    series: Object,
    mode: { type: String, default: "cumulative" },
    projectId: Number,
    budgetId: Number
  }

  connect() {
    this._chart = null
    this._renderedFired = false

    if (!this.hasCanvasTarget) return

    if (this._isVisible()) {
      this._init()
    } else {
      this._observe()
    }
  }

  disconnect() {
    this._destroy()
    this._observer?.disconnect()
    this._observer = null
  }

  // ── Cumulative / Per-Period toggle ─────────────────────────────────
  toggleMode(event) {
    const next = event.currentTarget?.dataset?.modeParam
    if (!next || next === this.modeValue) return
    this.modeValue = next
    this._syncToggleButtons()
    this._destroy()
    this._renderedFired = false
    this._init()
    try {
      track("Cash Flow Chart Mode Toggled", {
        mode: next,
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue
      })
    } catch { /* mixpanel optional */ }
  }

  _syncToggleButtons() {
    if (!this.hasToggleButtonTarget) return
    this.toggleButtonTargets.forEach(btn => {
      const active = btn.dataset.modeParam === this.modeValue
      btn.classList.toggle("bg-white", active)
      btn.classList.toggle("text-gray-900", active)
      btn.classList.toggle("shadow-sm", active)
      btn.classList.toggle("text-gray-500", !active)
    })
  }

  _isVisible() {
    const el = this.canvasTarget
    if (!el.isConnected) return false
    const rect = el.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  _observe() {
    if (typeof IntersectionObserver === "undefined") {
      requestAnimationFrame(() => this._init())
      return
    }
    this._observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        this._observer.disconnect()
        this._observer = null
        this._init()
      }
    }, { threshold: 0.1 })
    this._observer.observe(this.canvasTarget)
  }

  _init() {
    if (this._chart) return
    const s = this.seriesValue
    if (!s || !Array.isArray(s.buckets) || s.buckets.length === 0) return

    const labels = s.buckets.map(iso => this._formatLabel(iso, s.grain))
    const mode = this.modeValue === "per_period" ? "per_period" : "cumulative"

    const todayIdx = this._todayIndex(s.buckets)
    const endIdx = s.buckets.length - 1
    const verticalLines = this._verticalLinesPlugin([
      todayIdx >= 0 ? { index: todayIdx, color: "#2563eb", label: "Today" } : null,
      endIdx >= 0 ? { index: endIdx, color: "#f97316", label: "End" } : null
    ].filter(Boolean))

    const config = mode === "cumulative"
      ? this._cumulativeConfig(labels, s)
      : this._perPeriodConfig(labels, s)

    this._chart = new Chart(this.canvasTarget, {
      ...config,
      plugins: [verticalLines],
      options: this._sharedOptions(mode)
    })

    this._fireRendered(mode)
  }

  _cumulativeConfig(labels, s) {
    return {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Actual (cumulative)",
            data: s.cumulative_actual,
            borderColor: "#10b981",
            backgroundColor: "rgba(16,185,129,0.08)",
            borderWidth: 2,
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.2,
            spanGaps: true
          },
          {
            label: "Projected (cumulative)",
            data: s.cumulative_projected,
            borderColor: "#9ca3af",
            backgroundColor: "transparent",
            borderWidth: 1.5,
            borderDash: [6, 4],
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.2,
            spanGaps: true
          },
          {
            label: "Override (cumulative)",
            data: s.cumulative_override,
            borderColor: "#d97706",
            backgroundColor: "transparent",
            borderWidth: 1.5,
            borderDash: [2, 2],
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.2,
            spanGaps: true
          },
          {
            label: "Total (cumulative)",
            data: s.cumulative_total,
            borderColor: "#2563eb",
            backgroundColor: "rgba(37,99,235,0.1)",
            borderWidth: 2.5,
            fill: "origin",
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.2,
            spanGaps: true
          }
        ]
      }
    }
  }

  _perPeriodConfig(labels, s) {
    return {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            type: "bar",
            label: "Actual",
            data: s.actual,
            backgroundColor: "rgba(16,185,129,0.8)",
            borderColor: "#10b981",
            borderWidth: 0,
            stack: "period"
          },
          {
            type: "bar",
            label: "Projected",
            data: s.projected,
            backgroundColor: "rgba(156,163,175,0.6)",
            borderColor: "#9ca3af",
            borderWidth: 0,
            stack: "period"
          },
          {
            type: "bar",
            label: "Override",
            data: s.override,
            backgroundColor: "rgba(217,119,6,0.75)",
            borderColor: "#d97706",
            borderWidth: 0,
            stack: "period"
          },
          {
            type: "line",
            label: "Total",
            data: s.total,
            borderColor: "#2563eb",
            backgroundColor: "transparent",
            borderWidth: 2,
            fill: false,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0
          }
        ]
      }
    }
  }

  _sharedOptions(mode) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            font: { size: 13 },
            usePointStyle: true
          }
        },
        tooltip: {
          callbacks: {
            label: ctx => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
          }
        }
      },
      scales: {
        y: {
          stacked: mode === "per_period",
          ticks: { callback: v => formatShortCurrency(v), font: { size: 13 } },
          grid: { color: "#f3f4f6" }
        },
        x: {
          stacked: mode === "per_period",
          ticks: { font: { size: 13 } },
          grid: { display: false }
        }
      }
    }
  }

  _destroy() {
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  _fireRendered(mode) {
    if (this._renderedFired) return
    this._renderedFired = true
    try {
      const s = this.seriesValue || {}
      track("Cash Flow Chart Rendered", {
        grain: s.grain,
        mode,
        bucket_count: (s.buckets || []).length,
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue
      })
    } catch { /* mixpanel optional */ }
  }

  _formatLabel(iso, grain) {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const year = d.getUTCFullYear()
    const month = d.getUTCMonth()
    if (grain === "quarterly") {
      const q = Math.floor(month / 3) + 1
      return `Q${q} ${year}`
    }
    if (grain === "semi_annual") {
      return month <= 5 ? `H1 ${year}` : `H2 ${year}`
    }
    if (grain === "yearly") {
      return String(year)
    }
    return d.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })
  }

  _todayIndex(bucketIsoList) {
    const now = new Date()
    const todayMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    for (let i = 0; i < bucketIsoList.length; i++) {
      const ms = Date.parse(bucketIsoList[i])
      if (!Number.isNaN(ms) && ms >= todayMs) return i
    }
    return bucketIsoList.length - 1
  }

  _verticalLinesPlugin(lines) {
    return {
      id: "cashFlowVerticalLines",
      afterDatasetsDraw(chart) {
        const { ctx, chartArea, scales } = chart
        if (!chartArea) return
        lines.forEach(({ index, color, label }) => {
          const x = scales.x.getPixelForValue(index)
          if (x == null) return
          ctx.save()
          ctx.strokeStyle = color
          ctx.setLineDash([3, 3])
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(x, chartArea.top)
          ctx.lineTo(x, chartArea.bottom)
          ctx.stroke()
          ctx.setLineDash([])
          ctx.fillStyle = color
          ctx.font = "11px system-ui, -apple-system, sans-serif"
          ctx.textAlign = "center"
          ctx.fillText(label, x, chartArea.top - 4)
          ctx.restore()
        })
      }
    }
  }
}
